import axios from "axios";
import api, { fetchThumbnail } from "@/api";
import apiFetchNewsItems from "./fetchNewsItems";
import useMatomoSiteSearch, {
  getMatomoSiteSearchData
} from "@/composition/useMatomoSiteSearch";

const NUMBER_OF_ITEMS_TO_LOAD_MORE = 50;
let latestRequest;
const fetchNewsItems = async (
  { state, commit, rootState, rootGetters, dispatch },
  {
    type = "query",
    from = undefined,
    to = undefined,
    sortOrder = "desc",
    maxResults = 100,
    requestTime = performance.now()
  }: {
    type?: "query" | "poll" | "loadMore";
    from?: string;
    to?: string;
    filterRules?: any[] | [];
    sortOrder?: "desc" | "asc";
    maxResults?: number;
    requestTime?: number;
  }
) => {
  try {
    latestRequest = requestTime;
    commit("setSearchTimeStamp", latestRequest);

    if (type === "query") {
      commit("setIsLoading", true);
      commit("setNewsItems", []);
    }

    const authJWT = rootGetters["auth/validateAndRetrieveToken"]();
    if (!authJWT) {
      dispatch("auth/logout", undefined, { root: true });
    }

    const filterRules = [...rootState.filterRules.filterRules];

    const { response, remoteContent, newsItems } = await apiFetchNewsItems({
      url: `newsitems${type === "poll" ? "?autoRefresh=true" : ""}`,
      payload: {
        from,
        to,
        sortOrder,
        filterRules,
        maxResults:
          type === "loadMore" ? NUMBER_OF_ITEMS_TO_LOAD_MORE : maxResults,
        ...(type === "loadMore"
          ? { searchContinuationFields: state.searchContinuationFields }
          : {})
      },
      authJWT,
      version: "/api/v2"
    });

    const isLatestRequest = latestRequest === requestTime;

    if (type === "query" && isLatestRequest) {
      commit("setNewsItems", newsItems);
      commit("setRemoteContent", remoteContent);
      commit("setTotalResults", response.totalHits);
      commit("setSearchContinuationFields", response.searchContinuationFields);
    } else if (type === "loadMore" && isLatestRequest) {
      commit("mergeLoadMoreNewsItems", newsItems);
      commit("mergeRemoteContent", remoteContent);
      commit("setTotalResults", response.totalHits);
      commit("setSearchContinuationFields", response.searchContinuationFields);
    }

    if (type === "query") {
      commit("setIsLoading", false);

      const { keywords, numberOfResults } = getMatomoSiteSearchData(
        response,
        rootGetters["filterRules/getFilterRulesByType"]
      );

      if (keywords.length) {
        useMatomoSiteSearch({ keywords, numberOfResults });
      }
    }
  } catch (e) {
    console.error(e);
  }
};

const updateNewsItem = async ({ newsItem, authJWT }) => {
  try {
    const url = `newsitems/${newsItem.guid}`;
    await api.put({
      url,
      payload: newsItem,
      authJWT
    });
    return;
  } catch (e) {
    throw e;
  }
};

// bulk operation not being used
const fetchImages = async ({ commit, state }, authJWT) => {
  try {
    await state.remoteData.forEach(async remote => {
      const imageData = await fetchThumbnail(authJWT, remote.residref);
      commit("addImage", { imageData, residref: remote.residref });
    });
    return;
  } catch (e) {
    throw e;
  }
};

const fetchImage = async (
  { state, rootState, commit },
  { isThumbnail, residref, guid }
) => {
  const key = isThumbnail ? "thumbnail" : "preview";
  const { token } = rootState.auth;
  const encodedImage = state.encodedImages[guid];
  if (encodedImage && encodedImage[key]) return;
  try {
    const image = await axios.get(
      `https://cr.dpa-connect.de/v1/media/${
        isThumbnail ? "thumb/" : ""
      }${residref}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    commit("setEncodedImage", { key, data: image.data, guid });
  } catch (error) {
    console.error(error);
  }
};

const fetchAndStoreThumbnail = async (
  { commit, state },
  { authJWT, residref }
) => {
  try {
    const thumbnail = await fetchThumbnail(authJWT, residref);
    commit("addData", {
      data: thumbnail.data,
      residref
    });
    return;
  } catch (e) {
    throw e;
  }
};

const updateIsFavorite = async ({
  newsItem: { guid, isFavorite },
  authJWT
}) => {
  try {
    return await updateNewsItem({ newsItem: { guid, isFavorite }, authJWT });
  } catch (e) {
    throw e;
  }
};

const toggleFavorite = async ({ commit, state }, { newsItem, authJWT }) => {
  let index;
  try {
    const { newsItems } = state;
    index = newsItems.findIndex(item => item.guid === newsItem.guid);

    if (index > -1) {
      const updatedNewsItem = {
        ...newsItem,
        isFavorite: !newsItem.isFavorite
      };

      commit("updateNewsItem", {
        index,
        newsItem: updatedNewsItem
      });

      await updateIsFavorite({
        newsItem: updatedNewsItem,
        authJWT
      });
    }
  } catch (e) {
    commit("updateNewsItem", {
      index,
      newsItem
    });
    throw e;
  }
};

const downloadPDF = (attemptsLeft, uri) => {
  if (attemptsLeft > 0) {
    setTimeout(async () => {
      try {
        await axios.get(uri);
        window.location.href = uri;
      } catch (e) {
        // eslint-disable-next-line no-param-reassign
        attemptsLeft -= 1;
        downloadPDF(attemptsLeft, uri);
      }
    }, 500);
  } else {
    console.log("pdf couldn't be fetched");
  }
};

const bulkExportPDF = async ({ commit }, { selectedNewsItems, authJWT }) => {
  const items = selectedNewsItems.map(({ guid }) => ({
    newsItemId: guid
  }));
  api
    .post({
      url: "newsitems",
      payload: {
        items
      },
      authJWT,
      version: "/api/v2/pdf"
    })
    .then(res => {
      const PDFUri = res.uri;
      downloadPDF(20, PDFUri);
    })
    .catch(console.log);
};

export default {
  fetchNewsItems,
  toggleFavorite,
  fetchImages,
  fetchImage,
  fetchAndStoreThumbnail,
  bulkExportPDF
};
